import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { fetchAuthSession } from 'aws-amplify/auth'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import { useDispatcher } from '@/providers/DispatcherProvider'
import { useShifts } from '@/hooks/api/useShifts'
import {
  SOFTPHONE_QUERY_KEYS,
  useSoftphoneCredentials,
} from '@/hooks/api/useSoftphone'
import { softphoneService } from '@/api/services/softphoneService'
import {
  getSipCredentialsConnectionKey,
  SoftphoneSipClient,
} from '@/services/softphone/sipClient'
import {
  getSoftphoneCallErrorMessage,
  getSoftphoneErrorCode,
  getSoftphoneErrorText,
} from '@/services/softphone/errorMessages'
import {
  SOFTPHONE_REALTIME_SOCKET_ERROR_MESSAGE,
  SoftphoneRealtimeSocket,
  type SoftphoneRealtimeStatus,
} from '@/services/softphone/realtimeSocket'
import type {
  SoftphoneCallDirection,
  SoftphoneCredentials,
  SoftphoneCallSession,
  SoftphoneCallStatus,
  SoftphoneQueueStatus,
  SoftphoneRegistrationStatus,
  SoftphoneResponderTarget,
} from '@/types/api/softphone'

export type SoftphonePanelTab = 'history' | 'dialpad' | 'contacts'

const MAX_DIALPAD_NUMBER_LENGTH = 14
const COUNTRY_DIALING_CODES: Record<string, string> = {
  BENIN: '+229',
  BJ: '+229',
  CANADA: '+1',
  CA: '+1',
  CAMEROON: '+237',
  CM: '+237',
  "COTE D'IVOIRE": '+225',
  'COTE D IVOIRE': '+225',
  'IVORY COAST': '+225',
  CI: '+225',
  GAMBIA: '+220',
  GM: '+220',
  GHANA: '+233',
  GH: '+233',
  KENYA: '+254',
  KE: '+254',
  LIBERIA: '+231',
  LR: '+231',
  NIGERIA: '+234',
  NG: '+234',
  RWANDA: '+250',
  RW: '+250',
  SENEGAL: '+221',
  SN: '+221',
  'SIERRA LEONE': '+232',
  SL: '+232',
  'SOUTH AFRICA': '+27',
  ZA: '+27',
  TANZANIA: '+255',
  TZ: '+255',
  TOGO: '+228',
  TG: '+228',
  UGANDA: '+256',
  UG: '+256',
  'UNITED KINGDOM': '+44',
  UK: '+44',
  GB: '+44',
  'UNITED STATES': '+1',
  'UNITED STATES OF AMERICA': '+1',
  US: '+1',
}

const normalizeCountryKey = (value?: string | null) =>
  value
    ?.trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ') || ''

const normalizeDialingCode = (value?: string | null) => {
  const sanitized = value?.trim().replace(/[^\d+]/g, '')
  if (!sanitized) return null

  return sanitized.startsWith('+') ? sanitized : `+${sanitized}`
}

const getLocaleRegion = () => {
  if (typeof navigator === 'undefined') return null

  const locale = navigator.language || navigator.languages?.[0]
  if (!locale) return null

  try {
    return new Intl.Locale(locale).region || null
  } catch {
    const [, region] = locale.split(/[-_]/)
    return region || null
  }
}

const getSoftphoneDialingCode = (
  dispatcher?: {
    country?: string
    country_code?: string
    dispatch_center_country?: string
    dispatch_center_country_code?: string
    precinct_country?: string
    precinct_country_code?: string
    country_calling_code?: string
    phone_country_code?: string
    dialing_code?: string
  } | null
) => {
  const explicitDialingCode = normalizeDialingCode(
    dispatcher?.country_calling_code ||
      dispatcher?.phone_country_code ||
      dispatcher?.dialing_code
  )
  if (explicitDialingCode) return explicitDialingCode

  const dispatcherCountry =
    COUNTRY_DIALING_CODES[
      normalizeCountryKey(dispatcher?.dispatch_center_country)
    ] ||
    COUNTRY_DIALING_CODES[
      normalizeCountryKey(dispatcher?.dispatch_center_country_code)
    ] ||
    COUNTRY_DIALING_CODES[normalizeCountryKey(dispatcher?.precinct_country)] ||
    COUNTRY_DIALING_CODES[
      normalizeCountryKey(dispatcher?.precinct_country_code)
    ] ||
    COUNTRY_DIALING_CODES[normalizeCountryKey(dispatcher?.country)] ||
    COUNTRY_DIALING_CODES[normalizeCountryKey(dispatcher?.country_code)]
  if (dispatcherCountry) return dispatcherCountry

  const localeRegion = getLocaleRegion()
  return COUNTRY_DIALING_CODES[normalizeCountryKey(localeRegion)] || null
}

const normalizeDialpadInput = (value: string) => {
  let normalized = ''

  for (const character of value.trim()) {
    if (/\d/.test(character) || character === '*' || character === '#') {
      normalized += character
      continue
    }

    if (character === '+' && normalized.length === 0) {
      normalized += character
    }
  }

  return normalized.slice(0, MAX_DIALPAD_NUMBER_LENGTH)
}

const resolveDialpadPhoneNumber = (
  value: string,
  localDialingCode?: string | null
) => {
  const dialedNumber = normalizeDialpadInput(value)

  if (!dialedNumber || /[*#]/.test(dialedNumber)) {
    return dialedNumber
  }

  if (dialedNumber.startsWith('+')) {
    return dialedNumber
  }

  if (dialedNumber.startsWith('00') && dialedNumber.length > 2) {
    return `+${dialedNumber.slice(2)}`
  }

  const countryCode = normalizeDialingCode(localDialingCode)
  if (!countryCode) return dialedNumber

  const countryDigits = countryCode.replace(/\D/g, '')

  if (countryDigits && dialedNumber.startsWith(countryDigits)) {
    return `+${dialedNumber}`
  }

  const localNumber = dialedNumber.startsWith('0')
    ? dialedNumber.slice(1)
    : dialedNumber

  return `${countryCode}${localNumber}`
}

export interface SoftphoneActiveCall {
  callId?: string
  status: SoftphoneCallStatus
  direction: SoftphoneCallDirection
  target: string
  displayName?: string
  phoneNumber?: string
  startedAt?: string
  answeredAt?: string
  isMuted: boolean
  isHeld: boolean
}

type AudioContextConstructor = typeof AudioContext
type SoftphoneFeedbackKind = 'dialing' | 'failure' | 'incoming'

interface SoftphoneFeedbackPlayback {
  kind: SoftphoneFeedbackKind
  oscillators: OscillatorNode[]
  gain: GainNode
  timers: number[]
}

interface SoftphoneContextValue {
  isOpen: boolean
  activeTab: SoftphonePanelTab
  registrationStatus: SoftphoneRegistrationStatus
  realtimeStatus: SoftphoneRealtimeStatus
  isEligible: boolean
  isCheckingReadiness: boolean
  readinessMessage: string
  activeShiftId?: string
  activeCall: SoftphoneActiveCall | null
  attemptedCalls: SoftphoneCallSession[]
  error: string | null
  realtimeError: string | null
  dialpadNumber: string
  isAnsweringIncomingCall: boolean
  openWidget: (tab?: SoftphonePanelTab) => void
  closeWidget: () => void
  setActiveTab: (tab: SoftphonePanelTab) => void
  appendDialpadDigit: (digit: string) => void
  pasteDialpadNumber: (value: string) => void
  deleteDialpadDigit: () => void
  clearDialpadNumber: () => void
  retryConnection: () => void
  startResponderCall: (target: SoftphoneResponderTarget) => Promise<void>
  startDialpadCall: (phoneNumber: string) => Promise<void>
  answerIncomingCall: () => Promise<void>
  rejectIncomingCall: () => Promise<void>
  hangupCall: () => Promise<void>
  toggleMute: () => void
  toggleHold: () => Promise<void>
  transferActiveCall: (destination: string) => Promise<void>
}

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null)

const createIdempotencyKey = () => {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`

  return `dispatch-softphone-${random}`
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }

  return fallback
}

const getSipStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined

  const candidate = error as {
    sipStatusCode?: number | string
    statusCode?: number | string
    response?: { message?: { statusCode?: number | string } }
    message?: { statusCode?: number | string }
  }
  const statusCode =
    candidate.sipStatusCode ||
    candidate.response?.message?.statusCode ||
    candidate.message?.statusCode ||
    candidate.statusCode
  const parsedStatusCode = Number(statusCode)

  return Number.isFinite(parsedStatusCode) ? parsedStatusCode : undefined
}

const shouldFallbackToPstn = (error: unknown) => {
  const code = getSoftphoneErrorCode(error)
  const message = getSoftphoneErrorText(error).toLowerCase()
  const sipStatusCode = getSipStatusCode(error)

  return (
    code === 'endpoint_disabled' ||
    code === 'not_registered' ||
    code === 'not_connected' ||
    sipStatusCode === 404 ||
    sipStatusCode === 408 ||
    sipStatusCode === 410 ||
    sipStatusCode === 480 ||
    sipStatusCode === 484 ||
    sipStatusCode === 486 ||
    sipStatusCode === 503 ||
    sipStatusCode === 604 ||
    message.includes('endpoint disabled') ||
    message.includes('not registered') ||
    message.includes('not connected') ||
    message.includes('sip invite rejected') ||
    message.includes('404 not found') ||
    message.includes('408 request timeout') ||
    message.includes('410 gone') ||
    message.includes('480 temporarily unavailable') ||
    message.includes('484 address incomplete') ||
    message.includes('486 busy here') ||
    message.includes('503 service unavailable') ||
    message.includes('604 does not exist anywhere') ||
    message.includes('q.850;cause=1') ||
    message.includes('q.850;cause=3') ||
    message.includes('q.850;cause=18') ||
    message.includes('q.850;cause=20') ||
    message.includes('q.850;cause=21') ||
    message.includes('q.850;cause=34') ||
    message.includes('no route') ||
    message.includes('not found') ||
    message.includes('temporarily unavailable') ||
    message.includes('request timeout') ||
    message.includes('address incomplete') ||
    message.includes('does not exist anywhere') ||
    message.includes('callee unavailable')
  )
}

const isMissingSipSessionError = (error: unknown) =>
  getErrorMessage(error, '').toLowerCase().includes('session does not exist')

const getTokenIssuer = (token?: string | null): string | null => {
  if (!token) return null

  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { iss?: string }
    return payload.iss || null
  } catch {
    return null
  }
}

const getRealtimeSocketAuth = async () => {
  const session = await fetchAuthSession()
  const accessToken = session.tokens?.accessToken?.toString()
  const cognitoIssuer = getTokenIssuer(accessToken)

  if (!accessToken || !cognitoIssuer) {
    throw new Error('Softphone realtime authentication is unavailable')
  }

  return {
    accessToken,
    cognitoIssuer,
  }
}

const isSipWebsocketUrl = (value?: string): boolean => {
  if (!value) return false

  try {
    const url = new URL(value)
    return url.pathname === '/ws' && !url.hostname.includes('execute-api')
  } catch {
    return false
  }
}

const mapCallStatus = (status?: string): SoftphoneCallStatus => {
  switch (status) {
    case 'initiating':
    case 'dialing':
    case 'ringing':
    case 'answered':
    case 'held':
    case 'transferring':
    case 'completed':
    case 'rejected':
    case 'missed':
    case 'failed':
      return status
    default:
      return 'ringing'
  }
}

/*
const isWithinShiftWindow = (from?: string, to?: string) => {
  if (!from || !to) return false
  const start = new Date(from).getTime()
  const end = new Date(to).getTime()
  const now = Date.now()

  if (Number.isNaN(start) || Number.isNaN(end)) return false
  return now >= start && now <= end
}
*/

const mapCallDirection = (
  call: SoftphoneCallSession,
  current: SoftphoneActiveCall | null
): SoftphoneCallDirection => {
  if (call.direction === 'incoming' || call.direction === 'inbound') {
    return 'incoming'
  }
  if (call.direction === 'outgoing' || call.direction === 'outbound') {
    return 'outgoing'
  }

  if (current?.direction) return current.direction
  if (call.status === 'missed') return 'incoming'
  if (call.to_type === 'pstn' || call.to_user_type === 'responder') {
    return 'outgoing'
  }
  if (call.to || call.dial_target || call.to_user_id) return 'outgoing'

  return 'incoming'
}

const getCallTarget = (call: SoftphoneCallSession): string => {
  const direction = mapCallDirection(call, null)

  if (direction === 'outgoing') {
    return (
      call.to ||
      call.to_user_id ||
      call.dial_target ||
      call.sip_uri ||
      call.caller_id ||
      call.callerId ||
      'Unknown'
    )
  }

  return (
    call.caller_name ||
    call.caller_id ||
    call.callerId ||
    call.phone_number ||
    call.from ||
    call.from_user_id ||
    call.to ||
    call.to_user_id ||
    call.dial_target ||
    'Unknown Caller'
  )
}

const isGenericCallIdentity = (value?: string) =>
  !value ||
  /^(unknown(?: caller| user)?|anonymous|private)$/i.test(value.trim())

const mergeCallSession = (
  current: SoftphoneActiveCall | null,
  call: SoftphoneCallSession
): SoftphoneActiveCall => {
  const resolvedTarget = getCallTarget(call)
  const currentTarget = isGenericCallIdentity(current?.target)
    ? undefined
    : current?.target
  const currentDisplayName = isGenericCallIdentity(current?.displayName)
    ? undefined
    : current?.displayName

  return {
    callId: call.call_id || current?.callId,
    status: mapCallStatus(call.status) || current?.status || 'ringing',
    direction: mapCallDirection(call, current),
    target: currentTarget || resolvedTarget,
    displayName: currentDisplayName || resolvedTarget,
    phoneNumber:
      current?.phoneNumber ||
      call.phone_number ||
      call.caller_id ||
      call.callerId ||
      call.from ||
      call.to,
    startedAt: current?.startedAt || call.started_at || call.created_at,
    answeredAt: current?.answeredAt || call.answered_at,
    isMuted: current?.isMuted ?? false,
    isHeld: mapCallStatus(call.status) === 'held' || (current?.isHeld ?? false),
  }
}

export const useSoftphone = () => {
  const context = useContext(SoftphoneContext)
  if (!context) {
    throw new Error('useSoftphone must be used within SoftphoneProvider')
  }
  return context
}

export const SoftphoneProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const { dispatcher } = useDispatcher()
  const dispatcherId = dispatcher?.dispatcher_id
  const configuredRealtimeWebsocketUrl =
    import.meta.env.VITE_SOFTPHONE_REALTIME_WS_URL ||
    import.meta.env.VITE_NOTIFICATION_WEBSOCKET_URL
  const websocketUrl = isSipWebsocketUrl(configuredRealtimeWebsocketUrl)
    ? undefined
    : configuredRealtimeWebsocketUrl

  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const sipClientRef = useRef<SoftphoneSipClient | null>(null)
  const credentialsRef = useRef<SoftphoneCredentials | undefined>(undefined)
  const realtimeSocketRef = useRef<SoftphoneRealtimeSocket | null>(null)
  const isStartingCallRef = useRef(false)
  const isAnsweringCallRef = useRef(false)
  const audioRetryTimersRef = useRef<number[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const feedbackPlaybackRef = useRef<SoftphoneFeedbackPlayback | null>(null)
  const failedCallClearTimerRef = useRef<number | null>(null)
  const activeCallRef = useRef<SoftphoneActiveCall | null>(null)
  const localIncomingCallIdRef = useRef<string | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<SoftphonePanelTab>('history')
  const [registrationStatus, setRegistrationStatus] =
    useState<SoftphoneRegistrationStatus>('idle')
  const [sipRetryKey, setSipRetryKey] = useState(0)
  const [realtimeStatus, setRealtimeStatus] =
    useState<SoftphoneRealtimeStatus>('idle')
  const [realtimeRetryKey, setRealtimeRetryKey] = useState(0)
  const [realtimeError, setRealtimeError] = useState<string | null>(null)
  const [activeCall, setActiveCall] = useState<SoftphoneActiveCall | null>(null)
  const [isAnsweringIncomingCall, setIsAnsweringIncomingCall] = useState(false)
  const [attemptedCalls, setAttemptedCalls] = useState<SoftphoneCallSession[]>(
    []
  )
  const [dialpadNumber, setDialpadNumber] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    activeCallRef.current = activeCall
  }, [activeCall])
  const localDialingCode = useMemo(
    () => getSoftphoneDialingCode(dispatcher),
    [dispatcher]
  )

  const { data: shiftsData, refetch: refetchShifts } = useShifts(
    {
      dispatcher_id: dispatcherId,
      limit: 100,
      sortBy: 'from',
      sortOrder: 'desc',
    },
    {
      enabled: Boolean(isAuthenticated && dispatcherId),
      refetchInterval: 60 * 1000,
    }
  )

  const activeShift = useMemo(
    () =>
      shiftsData?.items?.find(
        shift =>
          shift.status === 'in-progress' &&
          shift.clock_in_status &&
          !shift.clock_out_status
        // && isWithinShiftWindow(shift.from, shift.to)
      ),
    [shiftsData?.items]
  )

  const isEligible = Boolean(isAuthenticated && dispatcher)
  const isCheckingReadiness = false

  const {
    data: credentials,
    error: credentialsError,
    refetch: refetchCredentials,
  } = useSoftphoneCredentials(isEligible)

  credentialsRef.current = credentials
  const sipCredentialsKey = getSipCredentialsConnectionKey(credentials)

  const readinessMessage = useMemo(() => {
    if (!isAuthenticated) return 'Sign in to use softphone'
    if (!dispatcher) return 'Loading dispatcher profile'
    if (isCheckingReadiness) return 'Checking softphone readiness'
    if (credentialsError instanceof Error) return credentialsError.message
    if (registrationStatus === 'registered') return 'Softphone ready'
    if (registrationStatus === 'connecting') return 'Connecting softphone'
    if (registrationStatus === 'error') return error || 'Softphone unavailable'
    return 'Preparing softphone'
  }, [
    credentialsError,
    dispatcher,
    error,
    isAuthenticated,
    isCheckingReadiness,
    registrationStatus,
  ])

  const invalidateSoftphoneActivityQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: query => {
        const [scope, type] = query.queryKey
        return scope === SOFTPHONE_QUERY_KEYS.all[0] && type !== 'credentials'
      },
    })
  }, [queryClient])

  const clearAudioRetryTimers = useCallback(() => {
    audioRetryTimersRef.current.forEach(timer => window.clearTimeout(timer))
    audioRetryTimersRef.current = []
  }, [])

  const clearFailedCallTimer = useCallback(() => {
    if (failedCallClearTimerRef.current === null) return
    window.clearTimeout(failedCallClearTimerRef.current)
    failedCallClearTimerRef.current = null
  }, [])

  const getAudioContext = useCallback((): AudioContext | null => {
    if (audioContextRef.current) return audioContextRef.current

    const AudioContextCtor =
      window.AudioContext ||
      (
        window as typeof window & {
          webkitAudioContext?: AudioContextConstructor
        }
      ).webkitAudioContext

    if (!AudioContextCtor) return null

    audioContextRef.current = new AudioContextCtor()
    return audioContextRef.current
  }, [])

  const unlockAudioFeedback = useCallback(async () => {
    const audioContext = getAudioContext()
    if (!audioContext) return

    if (audioContext.state === 'suspended') {
      await audioContext.resume().catch(() => {})
    }
  }, [getAudioContext])

  const stopFeedbackPlayback = useCallback(() => {
    const playback = feedbackPlaybackRef.current
    if (!playback) return

    playback.timers.forEach(timer => window.clearTimeout(timer))
    playback.oscillators.forEach(oscillator => {
      try {
        oscillator.stop()
      } catch {
        // The oscillator may already have ended naturally.
      }
      oscillator.disconnect()
    })
    playback.gain.disconnect()
    feedbackPlaybackRef.current = null
  }, [])

  const recordFailedCallAttempt = useCallback(
    ({
      target,
      displayName,
      phoneNumber,
      startedAt,
      caseId,
      callId,
    }: {
      target: string
      displayName?: string
      phoneNumber?: string
      startedAt?: string
      caseId?: string
      callId?: string
    }) => {
      // REST is the source of truth for call history. If session creation failed,
      // do not manufacture a local call that looks like it reached telephony.
      if (!callId) return

      const timestamp = startedAt || new Date().toISOString()
      const localCallId = callId

      const failedCall: SoftphoneCallSession = {
        call_id: localCallId,
        status: 'failed',
        direction: 'outgoing',
        actor_id: dispatcherId,
        from_user_id: dispatcherId,
        to: phoneNumber || target,
        caller_id: displayName || target,
        case_id: caseId,
        started_at: timestamp,
        created_at: timestamp,
        ended_at: new Date().toISOString(),
        duration_seconds: 0,
        metadata: {
          localOnly: true,
          failure_stage: 'start',
          backend_call_id: callId,
        },
      }

      setAttemptedCalls(current => [failedCall, ...current].slice(0, 25))
    },
    [dispatcherId]
  )

  const clearFailedCallAfterFeedback = useCallback(() => {
    clearFailedCallTimer()
    failedCallClearTimerRef.current = window.setTimeout(() => {
      setActiveCall(current => (current?.status === 'failed' ? null : current))
      setActiveTab('history')
      failedCallClearTimerRef.current = null
    }, 1300)
  }, [clearFailedCallTimer])

  const startDialingFeedback = useCallback(() => {
    stopFeedbackPlayback()
    const audioContext = getAudioContext()
    if (!audioContext) return
    if (audioContext.state === 'suspended') {
      void audioContext.resume().catch(() => {})
    }

    const gain = audioContext.createGain()
    gain.gain.setValueAtTime(0.045, audioContext.currentTime)
    gain.connect(audioContext.destination)

    const playback: SoftphoneFeedbackPlayback = {
      kind: 'dialing',
      oscillators: [],
      gain,
      timers: [],
    }
    feedbackPlaybackRef.current = playback

    const pulse = () => {
      if (feedbackPlaybackRef.current !== playback) return

      const oscillators = [440, 480].map(frequency => {
        const oscillator = audioContext.createOscillator()
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.connect(gain)
        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.75)
        playback.oscillators.push(oscillator)
        return oscillator
      })

      const cleanupTimer = window.setTimeout(() => {
        oscillators.forEach(oscillator => oscillator.disconnect())
        playback.oscillators = playback.oscillators.filter(
          oscillator => !oscillators.includes(oscillator)
        )
      }, 850)
      const nextTimer = window.setTimeout(pulse, 2600)
      playback.timers.push(cleanupTimer, nextTimer)
    }

    pulse()
  }, [getAudioContext, stopFeedbackPlayback])

  const startIncomingRinger = useCallback(() => {
    stopFeedbackPlayback()
    const audioContext = getAudioContext()
    if (!audioContext) return

    const gain = audioContext.createGain()
    gain.gain.setValueAtTime(0.16, audioContext.currentTime)
    gain.connect(audioContext.destination)

    const playback: SoftphoneFeedbackPlayback = {
      kind: 'incoming',
      oscillators: [],
      gain,
      timers: [],
    }
    feedbackPlaybackRef.current = playback

    const playBurst = () => {
      if (feedbackPlaybackRef.current !== playback) return
      ;[0, 420].forEach(offset => {
        const timer = window.setTimeout(() => {
          if (feedbackPlaybackRef.current !== playback) return

          const oscillators = [660, 880].map(frequency => {
            const oscillator = audioContext.createOscillator()
            oscillator.type = 'sine'
            oscillator.frequency.setValueAtTime(
              frequency,
              audioContext.currentTime
            )
            oscillator.connect(gain)
            oscillator.start()
            oscillator.stop(audioContext.currentTime + 0.32)
            playback.oscillators.push(oscillator)
            return oscillator
          })

          const cleanupTimer = window.setTimeout(() => {
            oscillators.forEach(oscillator => oscillator.disconnect())
            playback.oscillators = playback.oscillators.filter(
              oscillator => !oscillators.includes(oscillator)
            )
          }, 420)
          playback.timers.push(cleanupTimer)
        }, offset)
        playback.timers.push(timer)
      })

      const nextTimer = window.setTimeout(playBurst, 2200)
      playback.timers.push(nextTimer)
    }

    playBurst()
  }, [getAudioContext, stopFeedbackPlayback])

  const playCallFailureFeedback = useCallback(() => {
    stopFeedbackPlayback()
    const audioContext = getAudioContext()
    if (!audioContext) return

    const playback: SoftphoneFeedbackPlayback = {
      kind: 'failure',
      oscillators: [],
      gain: audioContext.createGain(),
      timers: [],
    }
    playback.gain.gain.setValueAtTime(0.08, audioContext.currentTime)
    playback.gain.connect(audioContext.destination)
    feedbackPlaybackRef.current = playback
    ;[440, 330, 220].forEach((frequency, index) => {
      const timer = window.setTimeout(() => {
        if (feedbackPlaybackRef.current !== playback) return
        const oscillator = audioContext.createOscillator()
        oscillator.type = 'triangle'
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
        oscillator.connect(playback.gain)
        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.16)
        playback.oscillators.push(oscillator)
      }, index * 170)
      playback.timers.push(timer)
    })

    const cleanupTimer = window.setTimeout(stopFeedbackPlayback, 720)
    playback.timers.push(cleanupTimer)
  }, [getAudioContext, stopFeedbackPlayback])

  const retryRemoteAudioPlayback = useCallback(() => {
    clearAudioRetryTimers()
    sipClientRef.current?.playRemoteAudio()
    ;[250, 1000, 2500].forEach(delay => {
      const timer = window.setTimeout(() => {
        sipClientRef.current?.playRemoteAudio()
      }, delay)
      audioRetryTimersRef.current.push(timer)
    })
  }, [clearAudioRetryTimers])

  useEffect(() => {
    const shouldRing =
      activeCall?.direction === 'incoming' && activeCall.status === 'ringing'

    if (shouldRing) {
      startIncomingRinger()
      return () => {
        if (feedbackPlaybackRef.current?.kind === 'incoming') {
          stopFeedbackPlayback()
        }
      }
    }

    if (feedbackPlaybackRef.current?.kind === 'incoming') {
      stopFeedbackPlayback()
    }
    return undefined
  }, [
    activeCall?.direction,
    activeCall?.status,
    startIncomingRinger,
    stopFeedbackPlayback,
  ])

  const openWidget = useCallback((tab?: SoftphonePanelTab) => {
    if (tab) setActiveTab(tab)
    setIsOpen(true)
  }, [])

  const closeWidget = useCallback(() => {
    setIsOpen(false)
  }, [])

  const resetConnections = useCallback(() => {
    clearAudioRetryTimers()
    clearFailedCallTimer()
    stopFeedbackPlayback()
    realtimeSocketRef.current?.close()
    realtimeSocketRef.current = null
    void sipClientRef.current?.disconnect()
    sipClientRef.current = null
    setRegistrationStatus('idle')
    setRealtimeStatus('idle')
    setRealtimeError(null)
  }, [clearAudioRetryTimers, clearFailedCallTimer, stopFeedbackPlayback])

  const handleRealtimeStatusChange = useCallback(
    (status: SoftphoneRealtimeStatus) => {
      setRealtimeStatus(status)

      if (status === 'connected') {
        setRealtimeError(null)
      }
    },
    []
  )

  useEffect(() => {
    if (credentialsError instanceof Error) {
      setRegistrationStatus('unavailable')
      setError(credentialsError.message)
    }
  }, [credentialsError])

  useEffect(() => {
    if (!isEligible) {
      resetConnections()
      setActiveCall(null)
      setError(null)
      return
    }

    const connectionCredentials = credentialsRef.current
    if (!connectionCredentials || !remoteAudioRef.current) return

    let cancelled = false
    const client = new SoftphoneSipClient(remoteAudioRef.current, {
      onRegistered: () => setRegistrationStatus('registered'),
      onUnregistered: () => setRegistrationStatus('disconnected'),
      onServerConnect: () => setRegistrationStatus('connecting'),
      onServerDisconnect: disconnectError => {
        setRegistrationStatus('disconnected')
        if (disconnectError) setError(disconnectError.message)
      },
      onIncomingCall: identity => {
        const timestamp = new Date().toISOString()
        const localCallId = `local-inbound-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`
        const phoneNumber = identity.phoneNumber || identity.user
        const target =
          phoneNumber ||
          identity.displayName ||
          identity.uri ||
          'Unknown Caller'
        const displayName = identity.displayName || target
        const localCall: SoftphoneCallSession = {
          call_id: localCallId,
          status: 'ringing',
          direction: 'incoming',
          from: phoneNumber || identity.uri,
          caller_id: phoneNumber || identity.displayName,
          caller_name: identity.displayName,
          phone_number: phoneNumber,
          started_at: timestamp,
          created_at: timestamp,
          metadata: { localOnly: true, source: 'sip_invite' },
        }

        localIncomingCallIdRef.current = localCallId
        setAttemptedCalls(current =>
          [
            localCall,
            ...current.filter(call => call.call_id !== localCallId),
          ].slice(0, 25)
        )
        setIsOpen(true)
        setActiveTab('history')
        setActiveCall(
          current =>
            current || {
              status: 'ringing',
              direction: 'incoming',
              target,
              displayName,
              phoneNumber,
              startedAt: timestamp,
              isMuted: false,
              isHeld: false,
            }
        )
      },
      onCallCreated: () => {
        setActiveCall(current =>
          current
            ? { ...current, status: current.status || 'dialing' }
            : current
        )
      },
      onCallAnswered: () => {
        stopFeedbackPlayback()
        retryRemoteAudioPlayback()
        setActiveCall(current =>
          current
            ? {
                ...current,
                status: 'answered',
                answeredAt: current.answeredAt || new Date().toISOString(),
              }
            : current
        )
        const localCallId = localIncomingCallIdRef.current
        if (localCallId) {
          setAttemptedCalls(current =>
            current.map(call =>
              call.call_id === localCallId
                ? {
                    ...call,
                    status: 'answered',
                    answered_at: call.answered_at || new Date().toISOString(),
                  }
                : call
            )
          )
        }
      },
      onCallHangup: () => {
        clearAudioRetryTimers()
        stopFeedbackPlayback()
        const localCallId = localIncomingCallIdRef.current
        const currentCall = activeCallRef.current
        if (localCallId) {
          const endedAt = new Date().toISOString()
          setAttemptedCalls(current =>
            current.map(call =>
              call.call_id === localCallId
                ? {
                    ...call,
                    status:
                      currentCall?.answeredAt ||
                      call.answered_at ||
                      call.status === 'answered'
                        ? 'completed'
                        : 'missed',
                    ended_at: endedAt,
                  }
                : call
            )
          )
          localIncomingCallIdRef.current = null
        }
        setActiveCall(null)
        invalidateSoftphoneActivityQueries()
      },
      onCallHold: held => {
        setActiveCall(current =>
          current
            ? {
                ...current,
                status: held ? 'held' : 'answered',
                isHeld: held,
              }
            : current
        )
      },
      onError: sipError => setError(sipError.message),
    })

    sipClientRef.current = client
    setRegistrationStatus('connecting')
    setError(null)

    client.connect(connectionCredentials).catch(connectError => {
      if (cancelled) return
      setRegistrationStatus('error')
      setError((connectError as Error).message)
    })

    return () => {
      cancelled = true
      if (sipClientRef.current === client) {
        sipClientRef.current = null
      }
      void client.disconnect()
    }
  }, [
    clearAudioRetryTimers,
    invalidateSoftphoneActivityQueries,
    isEligible,
    resetConnections,
    retryRemoteAudioPlayback,
    sipCredentialsKey,
    sipRetryKey,
    stopFeedbackPlayback,
  ])

  useEffect(() => {
    if (!isEligible || !dispatcherId) {
      realtimeSocketRef.current?.close()
      realtimeSocketRef.current = null
      setRealtimeError(null)
      return
    }

    const socket = new SoftphoneRealtimeSocket({
      websocketUrl,
      getAuth: getRealtimeSocketAuth,
      dispatcherId,
      onStatusChange: handleRealtimeStatusChange,
      onError: socketError =>
        setRealtimeError(
          socketError.message || SOFTPHONE_REALTIME_SOCKET_ERROR_MESSAGE
        ),
      onMessage: message => {
        const payload =
          message.data && typeof message.data === 'object'
            ? (message.data as Record<string, unknown>)
            : (message as Record<string, unknown>)
        const type =
          message.type ||
          (payload.type as string | undefined) ||
          message.message
        const call =
          (payload.call as SoftphoneCallSession | undefined) ||
          (payload.call_session as SoftphoneCallSession | undefined) ||
          (payload.session as SoftphoneCallSession | undefined) ||
          (payload.call_id
            ? (payload as unknown as SoftphoneCallSession)
            : undefined)

        if (call?.call_id) {
          setActiveCall(current => mergeCallSession(current, call))
          if (type?.includes('call_session_created')) {
            setIsOpen(true)
          }
        }

        if (type?.includes('queue_status')) {
          const pushedQueue = payload.queue as SoftphoneQueueStatus | undefined
          if (pushedQueue) {
            const agents = pushedQueue.agents || []
            const dispatcherIndex = dispatcherId
              ? agents.findIndex(agent => agent.user_id === dispatcherId)
              : -1
            const nextQueue: SoftphoneQueueStatus = {
              ...pushedQueue,
              callers: pushedQueue.callers || [],
              callerCount:
                pushedQueue.callerCount ?? pushedQueue.callers?.length ?? 0,
              dispatcherCount: pushedQueue.dispatcherCount ?? 0,
              dispatcherPosition:
                pushedQueue.dispatcherPosition ??
                (dispatcherIndex >= 0 ? dispatcherIndex + 1 : undefined),
            }

            queryClient.setQueryData(
              SOFTPHONE_QUERY_KEYS.queueStatus(dispatcherId),
              nextQueue
            )
          }
        }

        if (type?.includes('telephony_event')) {
          invalidateSoftphoneActivityQueries()
        }
      },
    })

    realtimeSocketRef.current = socket
    socket.connect()

    return () => {
      socket.close()
      if (realtimeSocketRef.current === socket) {
        realtimeSocketRef.current = null
      }
    }
  }, [
    dispatcherId,
    handleRealtimeStatusChange,
    invalidateSoftphoneActivityQueries,
    isEligible,
    queryClient,
    realtimeRetryKey,
    websocketUrl,
  ])

  const retryConnection = useCallback(() => {
    setError(null)
    setRealtimeError(null)
    resetConnections()
    setSipRetryKey(current => current + 1)
    setRealtimeRetryKey(current => current + 1)
    void refetchShifts()
    void refetchCredentials()
  }, [refetchCredentials, refetchShifts, resetConnections])

  const startResponderCall = useCallback(
    async (target: SoftphoneResponderTarget) => {
      openWidget('history')
      setError(null)
      let startedCallId: string | undefined
      const responderIsSipReachable = ['online', 'engaged'].includes(
        String(target.status || 'online').toLowerCase()
      )
      const dialMode = responderIsSipReachable ? 'user' : 'pstn'
      const destination = responderIsSipReachable
        ? target.responderId
        : target.phoneNumber

      if (!isEligible) {
        const message = 'Sign in to use softphone'
        setError(message)
        toast.error(message)
        return
      }

      if (!destination) {
        const message = 'No callable responder destination is available'
        setError(message)
        toast.error(message)
        return
      }

      if (registrationStatus !== 'registered' || !sipClientRef.current) {
        const message = 'Softphone is still connecting'
        setError(message)
        toast.error(message)
        return
      }

      if (isStartingCallRef.current) return
      isStartingCallRef.current = true
      clearFailedCallTimer()

      const sipClient = sipClientRef.current

      const optimisticCall: SoftphoneActiveCall = {
        status: 'initiating',
        direction: 'outgoing',
        target: target.phoneNumber || target.displayName,
        displayName: target.displayName,
        phoneNumber: target.phoneNumber,
        startedAt: new Date().toISOString(),
        isMuted: false,
        isHeld: false,
      }

      const startCallRoute = async (
        routeMode: 'user' | 'pstn',
        routeDestination: string
      ) => {
        startedCallId = undefined
        const callData = await softphoneService.startUserCall(
          {
            to_type: routeMode,
            to: routeDestination,
            ...(routeMode === 'user' && {
              to_user_type: 'responder' as const,
            }),
            callerId: 'ERA Dispatcher',
            caseId: target.caseId,
            recordCall: true,
          },
          createIdempotencyKey()
        )

        const dialTarget = callData.dial?.target

        if (!dialTarget) {
          throw new Error('The softphone API did not return a SIP target')
        }

        setActiveCall(current => ({
          ...(current || optimisticCall),
          callId: callData.call?.call_id,
          status: 'dialing',
        }))
        startedCallId = callData.call?.call_id

        await sipClient.call(dialTarget)
        invalidateSoftphoneActivityQueries()
      }

      try {
        await unlockAudioFeedback()
        startDialingFeedback()
        setActiveCall(optimisticCall)
        await startCallRoute(dialMode, destination)
        retryRemoteAudioPlayback()
      } catch (callError) {
        let finalCallError = callError

        if (
          dialMode === 'user' &&
          target.phoneNumber &&
          shouldFallbackToPstn(finalCallError)
        ) {
          if (startedCallId) {
            await softphoneService.hangUpCall(startedCallId).catch(() => {})
          }

          try {
            setError(null)
            setActiveCall(optimisticCall)
            await startCallRoute('pstn', target.phoneNumber)
            retryRemoteAudioPlayback()
            return
          } catch (fallbackError) {
            finalCallError = fallbackError
          }
        }

        if (startedCallId) {
          void softphoneService.hangUpCall(startedCallId).catch(() => {})
        }
        void sipClient.hangup().catch(() => {})
        recordFailedCallAttempt({
          target: optimisticCall.target,
          displayName: optimisticCall.displayName,
          phoneNumber: optimisticCall.phoneNumber,
          startedAt: optimisticCall.startedAt,
          caseId: target.caseId,
          callId: startedCallId,
        })
        playCallFailureFeedback()
        const message = getSoftphoneCallErrorMessage(finalCallError)
        setError(message)
        setActiveCall(current =>
          current
            ? {
                ...current,
                status: 'failed',
              }
            : current
        )
        clearFailedCallAfterFeedback()
        toast.error(message)
      } finally {
        isStartingCallRef.current = false
      }
    },
    [
      invalidateSoftphoneActivityQueries,
      isEligible,
      openWidget,
      playCallFailureFeedback,
      clearFailedCallAfterFeedback,
      clearFailedCallTimer,
      recordFailedCallAttempt,
      registrationStatus,
      retryRemoteAudioPlayback,
      startDialingFeedback,
      unlockAudioFeedback,
    ]
  )

  const answerIncomingCall = useCallback(async () => {
    if (!activeCall) return
    if (isAnsweringCallRef.current) return
    isAnsweringCallRef.current = true
    setIsAnsweringIncomingCall(true)
    setError(null)

    try {
      await sipClientRef.current?.answer()
      stopFeedbackPlayback()
      retryRemoteAudioPlayback()
      setActiveCall(current =>
        current
          ? {
              ...current,
              status: 'answered',
              answeredAt: new Date().toISOString(),
            }
          : current
      )
      if (activeCall.callId) {
        void softphoneService.acceptCall(activeCall.callId).catch(error => {
          setError(getErrorMessage(error, 'Unable to mark call answered'))
        })
      }
      invalidateSoftphoneActivityQueries()
    } catch (answerError) {
      if (isMissingSipSessionError(answerError)) {
        setActiveCall(null)
        invalidateSoftphoneActivityQueries()
        return
      }

      const message =
        answerError instanceof Error
          ? answerError.message
          : 'Unable to answer call'
      setError(message)
      toast.error(message)
    } finally {
      isAnsweringCallRef.current = false
      setIsAnsweringIncomingCall(false)
    }
  }, [
    activeCall,
    invalidateSoftphoneActivityQueries,
    retryRemoteAudioPlayback,
    stopFeedbackPlayback,
  ])

  const startDialpadCall = useCallback(
    async (rawPhoneNumber: string) => {
      openWidget('history')
      setError(null)
      let startedCallId: string | undefined
      const phoneNumber = resolveDialpadPhoneNumber(
        rawPhoneNumber,
        localDialingCode
      )

      if (!phoneNumber) {
        const message = 'Enter a phone number to call'
        setError(message)
        toast.error(message)
        return
      }

      if (!isEligible) {
        const message = 'Sign in to use softphone'
        setError(message)
        toast.error(message)
        return
      }

      if (registrationStatus !== 'registered' || !sipClientRef.current) {
        const message = 'Softphone is still connecting'
        setError(message)
        toast.error(message)
        return
      }

      if (isStartingCallRef.current) return
      isStartingCallRef.current = true
      clearFailedCallTimer()

      try {
        await unlockAudioFeedback()
        startDialingFeedback()
        const optimisticCall: SoftphoneActiveCall = {
          status: 'initiating',
          direction: 'outgoing',
          target: phoneNumber,
          phoneNumber,
          startedAt: new Date().toISOString(),
          isMuted: false,
          isHeld: false,
        }
        setActiveCall(optimisticCall)

        const callData = await softphoneService.startUserCall(
          {
            to_type: 'pstn',
            to: phoneNumber,
            callerId: 'ERA Dispatcher',
            recordCall: true,
          },
          createIdempotencyKey()
        )
        const dialTarget = callData.dial?.target

        if (!dialTarget) {
          throw new Error('The softphone API did not return a dial target')
        }

        setActiveCall(current => ({
          ...(current || optimisticCall),
          callId: callData.call?.call_id,
          status: 'dialing',
        }))
        startedCallId = callData.call?.call_id

        await sipClientRef.current.call(dialTarget)
        retryRemoteAudioPlayback()
        setDialpadNumber('')
        invalidateSoftphoneActivityQueries()
      } catch (callError) {
        if (startedCallId) {
          void softphoneService.hangUpCall(startedCallId).catch(() => {})
        }
        void sipClientRef.current?.hangup().catch(() => {})
        recordFailedCallAttempt({
          target: phoneNumber,
          phoneNumber,
          startedAt: new Date().toISOString(),
          callId: startedCallId,
        })
        playCallFailureFeedback()
        const message = getSoftphoneCallErrorMessage(callError)
        setError(message)
        setActiveCall(current =>
          current
            ? {
                ...current,
                status: 'failed',
              }
            : current
        )
        clearFailedCallAfterFeedback()
        toast.error(message)
      } finally {
        isStartingCallRef.current = false
      }
    },
    [
      invalidateSoftphoneActivityQueries,
      isEligible,
      openWidget,
      playCallFailureFeedback,
      clearFailedCallAfterFeedback,
      clearFailedCallTimer,
      localDialingCode,
      recordFailedCallAttempt,
      registrationStatus,
      retryRemoteAudioPlayback,
      startDialingFeedback,
      unlockAudioFeedback,
    ]
  )

  const rejectIncomingCall = useCallback(async () => {
    if (!activeCall) return

    const callId = activeCall.callId
    setError(null)
    clearAudioRetryTimers()
    stopFeedbackPlayback()
    setActiveCall(null)

    const cleanupTasks: Array<Promise<unknown>> = [
      sipClientRef.current?.decline() ?? Promise.resolve(),
    ]

    if (callId) {
      cleanupTasks.push(softphoneService.rejectCall(callId, 'busy'))
    }

    const results = await Promise.allSettled(cleanupTasks)
    const rejected = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )

    invalidateSoftphoneActivityQueries()

    if (rejected) {
      const message = getErrorMessage(rejected.reason, 'Unable to reject call')
      setError(message)
      toast.error(message)
    }
  }, [
    activeCall,
    clearAudioRetryTimers,
    invalidateSoftphoneActivityQueries,
    stopFeedbackPlayback,
  ])

  const hangupCall = useCallback(async () => {
    if (!activeCall) return

    const callId = activeCall.callId
    setError(null)
    clearAudioRetryTimers()
    stopFeedbackPlayback()
    setActiveCall(null)

    const cleanupTasks: Array<Promise<unknown>> = [
      sipClientRef.current?.hangup() ?? Promise.resolve(),
    ]

    if (callId) {
      cleanupTasks.push(softphoneService.hangUpCall(callId))
    }

    const results = await Promise.allSettled(cleanupTasks)
    const rejected = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )

    invalidateSoftphoneActivityQueries()

    if (rejected) {
      const message = getErrorMessage(rejected.reason, 'Unable to end call')
      setError(message)
      toast.error(message)
    }
  }, [
    activeCall,
    clearAudioRetryTimers,
    invalidateSoftphoneActivityQueries,
    stopFeedbackPlayback,
  ])

  const toggleMute = useCallback(() => {
    if (!activeCall || !sipClientRef.current) return

    if (activeCall.isMuted) {
      sipClientRef.current.unmute()
    } else {
      sipClientRef.current.mute()
    }

    setActiveCall(current =>
      current
        ? {
            ...current,
            isMuted: !current.isMuted,
          }
        : current
    )
  }, [activeCall])

  const toggleHold = useCallback(async () => {
    if (!activeCall || !sipClientRef.current) return

    try {
      if (activeCall.isHeld) {
        await sipClientRef.current.unhold()
      } else {
        await sipClientRef.current.hold()
      }

      setActiveCall(current =>
        current
          ? {
              ...current,
              status: current.isHeld ? 'answered' : 'held',
              isHeld: !current.isHeld,
            }
          : current
      )
      invalidateSoftphoneActivityQueries()
    } catch (holdError) {
      if (isMissingSipSessionError(holdError)) {
        setActiveCall(null)
        invalidateSoftphoneActivityQueries()
        return
      }

      const message =
        holdError instanceof Error ? holdError.message : 'Unable to update hold'
      setError(message)
      toast.error(message)
    }
  }, [activeCall, invalidateSoftphoneActivityQueries])

  const transferActiveCall = useCallback(
    async (destination: string) => {
      if (!activeCall?.callId) {
        const message = 'No active backend call session is available'
        setError(message)
        toast.error(message)
        return
      }

      const trimmedDestination = destination.trim()
      if (!trimmedDestination) {
        const message = 'Enter a transfer destination'
        setError(message)
        toast.error(message)
        return
      }

      try {
        await softphoneService.transferCall(
          activeCall.callId,
          trimmedDestination
        )
        setActiveCall(current =>
          current
            ? {
                ...current,
                status: 'transferring',
              }
            : current
        )
        invalidateSoftphoneActivityQueries()
        toast.success('Transfer requested')
      } catch (transferError) {
        const message =
          transferError instanceof Error
            ? transferError.message
            : 'Unable to transfer call'
        setError(message)
        toast.error(message)
      }
    },
    [activeCall?.callId, invalidateSoftphoneActivityQueries]
  )

  const appendDialpadDigit = useCallback((digit: string) => {
    setDialpadNumber(current => {
      const remainingLength = MAX_DIALPAD_NUMBER_LENGTH - current.length
      if (remainingLength <= 0) return current

      return `${current}${digit.slice(0, remainingLength)}`
    })
  }, [])

  const pasteDialpadNumber = useCallback((value: string) => {
    setDialpadNumber(normalizeDialpadInput(value))
  }, [])

  const deleteDialpadDigit = useCallback(() => {
    setDialpadNumber(current => current.slice(0, -1))
  }, [])

  const clearDialpadNumber = useCallback(() => {
    setDialpadNumber('')
  }, [])

  const value: SoftphoneContextValue = {
    isOpen,
    activeTab,
    registrationStatus,
    realtimeStatus,
    isEligible,
    isCheckingReadiness,
    readinessMessage,
    activeShiftId: activeShift?.shift_id,
    activeCall,
    attemptedCalls,
    error,
    realtimeError,
    dialpadNumber,
    isAnsweringIncomingCall,
    openWidget,
    closeWidget,
    setActiveTab,
    appendDialpadDigit,
    pasteDialpadNumber,
    deleteDialpadDigit,
    clearDialpadNumber,
    retryConnection,
    startResponderCall,
    startDialpadCall,
    answerIncomingCall,
    rejectIncomingCall,
    hangupCall,
    toggleMute,
    toggleHold,
    transferActiveCall,
  }

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay className="sr-only" />
    </SoftphoneContext.Provider>
  )
}
